import React, { useEffect, useState } from "react";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import InputLabel from "../Builder/nodes/Logic/Inputs/InputLabel";
import { InboundCampaignType } from "../store/inboundCampaignSlice";
import { pageProps } from "../helpers/pageProps";

const {
  baseUrl: baseURL,
  defaultCountry,
  countries,
  emailSuffix,
  accountEmails,
} = pageProps;

interface InboundSelectorProps {
  inboundCampaign: Partial<InboundCampaignType>;
  setInboundCampaign: React.Dispatch<
    React.SetStateAction<Partial<InboundCampaignType>>
  >;
}

export default function InboundSelector({
  inboundCampaign,
  setInboundCampaign,
}: InboundSelectorProps) {
  const [countryISO, setCountryISO] = useState(defaultCountry.country_code);
  const [inboundNumbers, setInboundNumbers] = useState([]);
  const [inboundEmails, setInboundEmails] = useState(accountEmails || []);
  const [error, setError] = useState("");

  // Extract the email suffix without domain for reuse
  const emailSuffixWithoutDomain = pageProps.emailSuffix.replace(
    "@" + pageProps.emailDomain,
    ""
  );

  // get inbound numbers when country or channel changes
  useEffect(() => {
    if (inboundCampaign.Channel === "email") {
      return;
    }

    const abortController = new AbortController();

    (async () => {
      try {
        const res = await fetch(
          `${baseURL}/workflows-api-numbers?country_iso=${countryISO}&sms=${
            inboundCampaign.Channel === "sms"
          }`,
          { signal: abortController.signal }
        );
        if (res.ok) {
          const data = await res.json();
          setInboundNumbers(data);
        }
      } catch (err: any) {
        if (err.name !== "AbortError") {
          console.log(err);
        }
      }
    })();

    return () => abortController.abort();
  }, [countryISO, inboundCampaign.Channel]);

  return (
    <div className="bg-purple-gradient-darker p-3 rounded-3">
      <div className="mb-3 row">
        <div className="col-auto">
          <Input
            label="Channel"
            inputType="radio"
            radioName="channel"
            isValidNewOption={() => false}
            value={[inboundCampaign.Channel]}
            onChange={([v]: any) => {
              setInboundCampaign({
                ...inboundCampaign,
                Channel: v,
                Inbound: "",
              });
            }}
            options={[
              { value: "sms", label: "SMS" },
              // { value: "voice", label: "Voice" },
              { value: "email", label: "Email" },
            ]}
          />
        </div>
        {(inboundCampaign.Channel === "sms" ||
          inboundCampaign.Channel === "voice") && (
          <div className="col-auto">
            <Input
              label="Country"
              inputType="select"
              value={[countryISO]}
              onChange={(options: any) => {
                setCountryISO(options[0]);
              }}
              options={countries.map((c: any) => ({
                value: c.country_code,
                label: c.country_name,
              }))}
            />
          </div>
        )}
      </div>

      {(inboundCampaign.Channel === "sms" ||
        inboundCampaign.Channel === "voice") && (
        <div className="mb-3">
          <InputLabel>Inbound number</InputLabel>
          <div className="d-flex">
            <div className="flex-grow-1">
              <Input
                inputType="select"
                value={[inboundCampaign.Inbound]}
                isValidNewOption={() => false}
                onChange={([v]: any) => {
                  setInboundCampaign({ ...inboundCampaign, Inbound: v });
                }}
                options={inboundNumbers.map((n) => ({
                  value: n,
                  label: n,
                }))}
              />
            </div>
          </div>
        </div>
      )}
      {inboundCampaign.Channel === "email" && (
        <>
          <InputLabel>Inbound email</InputLabel>
          <div className="d-flex">
            <div className="flex-grow-1" style={{ minWidth: 250 }}>
              <Input
                inputType="select"
                value={[
                  inboundCampaign.Inbound?.replace(
                    emailSuffixWithoutDomain,
                    ""
                  ) || "",
                ]}
                maxLength={50 as any}
                onChange={(options: any) => {
                  setInboundCampaign({
                    ...inboundCampaign,
                    Inbound: options[0] + emailSuffixWithoutDomain,
                  });
                }}
                onCreateOption={
                  ((newOption: any) => {
                    setInboundEmails((prev: any) => [...prev, newOption]);
                    setInboundCampaign({
                      ...inboundCampaign,
                      Inbound: newOption + emailSuffixWithoutDomain,
                    });
                  }) as any
                }
                isValidNewOption={(emailString) => {
                  const re = /^[a-zA-Z]([a-zA-Z\d_.]{0,49})?$/;
                  const test = re.test(emailString);
                  if (emailString) {
                    if (test) {
                      setError("");
                    } else {
                      setError(
                        "Invalid email. Use only letters, numbers, '_' or '.' and start with a letter. (max 50 characters)"
                      );
                    }
                  }
                  return test;
                }}
                options={inboundEmails
                  .filter((e: any) => e)
                  .map((n: any) => ({ value: n, label: n }))}
              />
            </div>
            <div className="pb-2 fw-2 input-group-text rounded-start-0">
              {emailSuffix}
            </div>
          </div>
        </>
      )}
      {error && <div className="text-danger text-center mt-2">{error}</div>}
    </div>
  );
}
